import { Button } from "@/components/ui/baseUi/button"
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor,
} from "@/components/ui/baseUi/combobox"
import { Input } from "@/components/ui/baseUi/input"
import { Text } from "@/components/ui/baseUi/text"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { type CoachActivityTypeEnum, getCoachActivitiesSortedByLabel } from "@/modules/coach/coachActivityTypes"
import { coachFormDataDefaultValues } from "@/modules/coach/types/coachFormTypes"
import { word } from "@/modules/core/utils/i18n"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { Plus } from "lucide-react"
import { useMemo } from "react"

const FREE_ACTIVITY_MAX_LENGTH = 25
const MAX_ACTIVITIES = 5

export const CoachCreationActivityStep = withForm({
  defaultValues: coachFormDataDefaultValues,
  props: {
    selectedActivities: [] as CoachActivityTypeEnum[],
    setSelectedActivities: (_: CoachActivityTypeEnum[]) => {
      return
    },
    otherActivities: [] as string[],
    addOtherActivity: () => {
      return
    },
    updateOtherActivity: (_i: number, _v: string) => {
      return
    },
    removeOtherActivity: (_i: number) => {
      return
    },
    activityError: null as string | null,
    setActivityError: (_: string | null) => {
      return
    },
  },
  render: function CoachCreationActivityStepInner({
    form,
    selectedActivities,
    setSelectedActivities,
    otherActivities,
    addOtherActivity,
    updateOtherActivity,
    removeOtherActivity,
    activityError,
    setActivityError,
  }) {
    const language = useLanguage()
    const chipsAnchor = useComboboxAnchor()
    const coachActivitiesSortedByLabel = useMemo(() => getCoachActivitiesSortedByLabel(language), [language])
    const canAddActivity = selectedActivities.length + otherActivities.length < MAX_ACTIVITIES

    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col">
          <Text fontStyle="HM" className="font-bold text-textOnSurface-default">
            {word("coach.onboarding.activity.title")}
          </Text>
          <Text fontStyle="BM" className="font-semibold text-textOnSurface-muted">
            {word("coach.onboarding.activity.subtitle")}
          </Text>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <form.AppField name="role">
            {(field) => (
              <field.Field>
                <field.Label fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                  {word("coach.admin.vitrine.header.role_title")}
                  <field.Label fontStyle="LM" className="text-feedback-negative-intense">
                    *
                  </field.Label>
                </field.Label>
                <field.Content>
                  <field.Input placeholder={word("coach.admin.vitrine.header.role_placeholder")} />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField name="yearsOfExperience">
            {(field) => (
              <field.Field>
                <field.Label fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                  {word("coach.admin.vitrine.header.years_experience")}
                  <field.Label fontStyle="LM" className="text-feedback-negative-intense">
                    *
                  </field.Label>
                </field.Label>
                <field.Content>
                  <field.Input type="number" min={0} max={99} />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <form.AppField name="city">
            {(field) => (
              <field.Field>
                <field.Label fontStyle="LM" className="text-textOnSurface-muted uppercase">
                  {word("coach.admin.vitrine.header.city")}
                </field.Label>
                <field.Content>
                  <field.Input placeholder={word("coach.admin.vitrine.header.city_placeholder")} />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField name="region">
            {(field) => (
              <field.Field>
                <field.Label fontStyle="LM" className="text-textOnSurface-muted uppercase">
                  {word("coach.admin.vitrine.header.region")}
                </field.Label>
                <field.Content>
                  <field.Input placeholder={word("coach.admin.vitrine.header.region_placeholder")} />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField name="country">
            {(field) => (
              <field.Field>
                <field.Label fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                  {word("coach.admin.vitrine.header.country")}
                  <field.Label fontStyle="LM" className="text-feedback-negative-intense">
                    *
                  </field.Label>
                </field.Label>
                <field.Content>
                  <field.CountrySelect />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>
        </div>

        <div className="flex flex-col gap-3 rounded-xl bg-surface-background-secondary p-4">
          <Text fontStyle="LM" className="text-textOnSurface-muted uppercase">
            {word("coach.admin.vitrine.header.activity_type")}
            <Text fontStyle="LM" className="text-feedback-negative-intense">
              {" "}
              *
            </Text>
          </Text>

          <Combobox
            value={selectedActivities}
            onValueChange={(vals) => {
              if (vals.length <= MAX_ACTIVITIES - otherActivities.length) {
                setSelectedActivities(vals)
                if (vals.length + otherActivities.length > 0) setActivityError(null)
              }
            }}
            multiple
          >
            <ComboboxChips ref={chipsAnchor}>
              {selectedActivities.map((activity) => (
                <ComboboxChip key={activity}>{word(`coach.activity.${activity}`)}</ComboboxChip>
              ))}
              <ComboboxChipsInput
                placeholder={
                  selectedActivities.length === 0 ? word("coach.admin.vitrine.header.activity_placeholder") : undefined
                }
              />
            </ComboboxChips>
            <ComboboxContent anchor={chipsAnchor}>
              <ComboboxList>
                {coachActivitiesSortedByLabel.map((activity) => (
                  <ComboboxItem key={activity} value={activity}>
                    {word(`coach.activity.${activity}`)}
                  </ComboboxItem>
                ))}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>

          {otherActivities.map((value, index) => (
            <div key={index} className="flex gap-2">
              <Input
                type="text"
                value={value}
                maxLength={FREE_ACTIVITY_MAX_LENGTH}
                onChange={(e) => {
                  updateOtherActivity(index, e.target.value)
                }}
                placeholder={word("coach.admin.vitrine.header.activity_placeholder")}
              />
              <button
                type="button"
                onClick={() => {
                  removeOtherActivity(index)
                }}
                className="shrink-0 cursor-pointer text-textOnSurface-muted hover:text-textOnSurface-default"
              >
                ✕
              </button>
            </div>
          ))}

          {canAddActivity && (
            <Button type="button" variant="secondary" className="w-full gap-2" onClick={addOtherActivity}>
              <Plus className="size-4" />
              {word("coach.admin.vitrine.header.add_activity")}
            </Button>
          )}

          {activityError && (
            <Text fontStyle="LS" className="text-feedback-negative-intense">
              {activityError}
            </Text>
          )}
        </div>
      </div>
    )
  },
})
